import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import PlacowkaFilterModal from '@/filter-modals/components/PlacowkaFilterModal'
import TrenerFilterModal from '@/filter-modals/components/TrenerFilterModal'
import StatusFilterModal from '@/filter-modals/components/StatusFilterModal'
import TrainingsTable from './components/TrainingsTable'
import useTrainingsData from './hooks/useTrainingsData'

export default function Trainings() {
	const { t } = useTranslation()
	const [isPlacowkaFilterOpen, setIsPlacowkaFilterOpen] = useState(false)
	const [isTrenerFilterOpen, setIsTrenerFilterOpen] = useState(false)
	const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false)
	const [selectedTrainerId, setSelectedTrainerId] = useState(null)
	const [selectedStatus, setSelectedStatus] = useState('')

	const {
		data: trainingsData,
		loading,
		loadingMore,
		totalCount,
		hasMore,
		handleLoadMore,
		handleSort,
		handleSearchChange,
		handleDelete,
		sortColumn,
		sortDirection,
		searchQuery,
		updateFilters,
		initialPlacowkaId,
	} = useTrainingsData()
	const [selectedPlacowkaId, setSelectedPlacowkaId] = useState(initialPlacowkaId)

	const statusOptions = useMemo(() => [
		{ value: 'new', label: t('trainings.list.statusOptions.new') },
		{ value: 'assigned', label: t('trainings.list.statusOptions.assigned') },
		{ value: 'completed', label: t('trainings.list.statusOptions.completed') },
		{ value: 'approved', label: t('trainings.list.statusOptions.approved') },
		{ value: 'paid', label: t('trainings.list.statusOptions.paid') },
	], [t])

	const handleApplyStatusFilter = useCallback(status => {
		setSelectedStatus(status || '')
		updateFilters({ status: status || null })
		setIsStatusFilterOpen(false)
	}, [updateFilters])

	const handleApplyPlacowkaFilter = useCallback(placowkaId => {
		updateFilters({ placowkaId: placowkaId || null })
		setSelectedPlacowkaId(placowkaId || null)
		setIsPlacowkaFilterOpen(false)
	}, [updateFilters])

	const handleApplyTrenerFilter = useCallback(trainerId => {
		updateFilters({ trainerId: trainerId || null })
		setSelectedTrainerId(trainerId || null)
		setIsTrenerFilterOpen(false)
	}, [updateFilters])

	const buttons = [
		{
			type: 'add',
			to: '/centrumSzkolen/szkolenia/add',
			label: t('trainings.list.actions.addNew'),
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder={t('trainings.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<TrainingsTable
				trainingsData={trainingsData}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				onDelete={handleDelete}
				hasPlacowkaFilter={!!selectedPlacowkaId}
				onOpenPlacowkaFilter={() => setIsPlacowkaFilterOpen(true)}
				hasTrenerFilter={!!selectedTrainerId}
				onOpenTrenerFilter={() => setIsTrenerFilterOpen(true)}
				hasStatusFilter={!!selectedStatus}
				onOpenStatusFilter={() => setIsStatusFilterOpen(true)}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={trainingsData.length}
				totalCount={totalCount}
			/>

			<StatusFilterModal
				isOpen={isStatusFilterOpen}
				onClose={() => setIsStatusFilterOpen(false)}
				selectedStatus={selectedStatus}
				onApply={handleApplyStatusFilter}
				statusOptions={statusOptions}
				title={t('trainings.list.statusFilter.title')}
				description={t('trainings.list.statusFilter.prompt')}
				applyLabel={t('trainings.list.statusFilter.apply')}
				clearLabel={t('trainings.list.statusFilter.clear')}
			/>

			<PlacowkaFilterModal
				isOpen={isPlacowkaFilterOpen}
				onClose={() => setIsPlacowkaFilterOpen(false)}
				selectedPlacowkaId={selectedPlacowkaId}
				onApply={handleApplyPlacowkaFilter}
				title={t('trainings.list.filters.placowkaTitle')}
				description={t('trainings.list.filters.placowkaDescription')}
				applyLabel={t('trainings.list.filters.apply')}
				clearLabel={t('trainings.list.filters.clear')}
			/>

			<TrenerFilterModal
				isOpen={isTrenerFilterOpen}
				onClose={() => setIsTrenerFilterOpen(false)}
				selectedTrainerId={selectedTrainerId}
				onApply={handleApplyTrenerFilter}
				title={t('trainings.list.filters.trenerTitle')}
				description={t('trainings.list.filters.trenerDescription')}
				applyLabel={t('trainings.list.filters.apply')}
				clearLabel={t('trainings.list.filters.clear')}
			/>
		</>
	)
}
